import { useEffect, useRef, useState } from "react";
import { useDashboardSync, DIM_META, type Dimension } from "../../lib/dashboardSync";
import { useCardExpand } from "../../lib/cardExpand";
import { ExpandButton } from "./CardModal";
import { api } from "../../lib/api";

// Global THREE from CDN script tag in index.html
declare global {
  interface Window {
    THREE: any;
  }
}

type Props = { ywi: any; _expanded?: boolean };

type PainPoint = {
  id: string;
  x_norm: number;
  y_norm: number;
  level: number;
  description: string | null;
  region?: string | null;
};

// A pending point being placed (not yet saved) — holds normalized coords while
// the member fills in level + description.
type PendingPoint = { x_norm: number; y_norm: number };

const DIM_POSITIONS: Record<Dimension, { yPct: number; xOff: number; zOff: number; r: number }> = {
  mental:    { yPct: 0.92, xOff: 0,    zOff: 0.12, r: 0.15 },
  emotional: { yPct: 0.70, xOff: 0,    zOff: 0.10, r: 0.16 },
  spiritual: { yPct: 0.55, xOff: 0,    zOff: 0.08, r: 0.14 },
  physical:  { yPct: 0.80, xOff: 0.22, zOff: 0,    r: 0.12 },
  social:    { yPct: 0.80, xOff: -0.22,zOff: 0,    r: 0.12 },
};

// Map a 1-10 level to a color (green→amber→red).
function levelColor(level: number): string {
  if (level <= 3) return "#2A9D8F";  // teal — mild
  if (level <= 6) return "#F4A261";  // amber — moderate
  return "#E76F51";                  // coral — severe
}

export function BodyModelCard({ ywi, _expanded }: Props) {
  const { expand } = useCardExpand();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<any>({});
  const [mode, setMode] = useState<"view" | "pain">("view");

  // Saved pain points for today (loaded from DB when entering Pain mode).
  const [painPoints, setPainPoints] = useState<PainPoint[]>([]);
  const [painLoading, setPainLoading] = useState(false);
  // A point being placed: normalized coords + the entry form.
  const [pending, setPending] = useState<PendingPoint | null>(null);
  const [formLevel, setFormLevel] = useState(5);
  const [formDesc, setFormDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const { active, setActive } = useDashboardSync();
  const activeRef = useRef(active);
  const modeRef = useRef(mode);

  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Load today's active pain points whenever we enter Pain mode.
  useEffect(() => {
    if (mode !== "pain") { setPending(null); return; }
    let cancelled = false;
    setPainLoading(true);
    api.pain.active()
      .then((r) => { if (!cancelled) setPainPoints(r.points ?? []); })
      .catch(() => { if (!cancelled) setPainPoints([]); })
      .finally(() => { if (!cancelled) setPainLoading(false); });
    return () => { cancelled = true; };
  }, [mode]);

  // Init Three.js scene
  useEffect(() => {
    if (!window.THREE) {
      console.warn("[BodyModelCard] THREE not loaded yet");
      return;
    }
    const THREE = window.THREE;
    const canvas = canvasRef.current!;
    const container = containerRef.current!;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 200);
    camera.position.set(0, 0.5, 12);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0, 0);
    renderer.outputEncoding = THREE.sRGBEncoding;

    const bodyGroup = new THREE.Group();
    scene.add(bodyGroup);

    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const dl = new THREE.DirectionalLight(0xA8DADC, 0.5);
    dl.position.set(3, 5, 5);
    scene.add(dl);

    // Hotspots: invisible hit-targets + visible dots/rings (dimension hotspots)
    const pts: Record<Dimension, any> = {} as any;
    const hitArr: any[] = [];

    (Object.keys(DIM_POSITIONS) as Dimension[]).forEach((k) => {
      const color = parseInt(DIM_META[k].color.slice(1), 16);
      const d = DIM_POSITIONS[k];
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.05, 12, 8),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6 })
      );
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.07, 0.12, 20),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.1, side: THREE.DoubleSide })
      );
      const hit = new THREE.Mesh(
        new THREE.SphereGeometry(d.r, 8, 6),
        new THREE.MeshBasicMaterial({ visible: false })
      );
      hit.userData.dim = k;
      hitArr.push(hit);
      bodyGroup.add(dot);
      bodyGroup.add(ring);
      bodyGroup.add(hit);
      pts[k] = { dot, ring, hit, d };
    });

    // Load GLB model
    const loader = new THREE.GLTFLoader();
    loader.load(
      "/assets/body-model.glb",
      (gltf: any) => {
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const s = 4 / size.y;
        model.scale.set(s, s, s);

        const box2 = new THREE.Box3().setFromObject(model);
        const c2 = box2.getCenter(new THREE.Vector3());
        model.position.sub(c2);

        model.traverse((ch: any) => {
          if (ch.isMesh && ch.material) {
            ch.material.transparent = true;
            ch.material.opacity = Math.min(ch.material.opacity || 1, 0.85);
          }
        });

        bodyGroup.add(model);

        const b = new THREE.Box3().setFromObject(model);
        const mn = b.min.y;
        const mx = b.max.y;
        const h = mx - mn;

        (Object.keys(pts) as Dimension[]).forEach((k) => {
          const p = pts[k];
          const d = p.d;
          const y = mn + h * d.yPct;
          const x = d.xOff * h;
          const z = d.zOff * h;
          p.dot.position.set(x, y, z);
          p.ring.position.set(x, y, z);
          p.hit.position.set(x, y, z);
        });

        camera.position.set(0, (mn + mx) / 2, h * 2.2);
        camera.lookAt(0, (mn + mx) / 2, 0);
      },
      undefined,
      (err: any) => console.error("[BodyModelCard] GLB load failed:", err)
    );

    // Raycasting for hover/click on dimension hotspots
    const rc = new THREE.Raycaster();
    const mo = new THREE.Vector2();

    const getHit = (e: PointerEvent | MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      mo.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      mo.y = -((e.clientY - r.top) / r.height) * 2 + 1;
      rc.setFromCamera(mo, camera);
      return rc.intersectObjects(hitArr);
    };

    const onMove = (e: PointerEvent) => {
      if (modeRef.current === "pain") return;
      const h = getHit(e);
      if (h.length) {
        setActive(h[0].object.userData.dim);
        canvas.style.cursor = "pointer";
      } else if (activeRef.current) {
        setActive(null);
        canvas.style.cursor = "default";
      }
    };

    const onClick = (e: MouseEvent) => {
      if (modeRef.current === "pain") {
        // Record a NORMALIZED coordinate (0..1) so the dot re-places at any size.
        const r = canvas.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        if (x < 0 || x > 1 || y < 0 || y > 1) return;
        setPending({ x_norm: x, y_norm: y });
        setFormLevel(5);
        setFormDesc("");
        return;
      }
      const h = getHit(e);
      if (h.length) setActive(h[0].object.userData.dim);
    };

    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("click", onClick);

    const resize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w <= 0 || h <= 0) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    resize();
    window.addEventListener("resize", resize);

    // Animate loop
    let t = 0;
    let raf = 0;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      t += 0.003;
      // Freeze rotation in pain mode so placed dots stay aligned to the body.
      if (modeRef.current !== "pain") {
        bodyGroup.rotation.y = Math.sin(t) * 0.3;
      } else {
        bodyGroup.rotation.y = 0;
      }
      const a = activeRef.current;
      (Object.keys(pts) as Dimension[]).forEach((k, idx) => {
        const p = pts[k];
        p.ring.lookAt(camera.position);
        // Hide dimension hotspots entirely in pain mode.
        const hidden = modeRef.current === "pain";
        if (hidden) {
          p.dot.material.opacity = 0;
          p.ring.material.opacity = 0;
          return;
        }
        if (a === k) {
          p.dot.material.opacity = 1;
          p.dot.scale.set(2, 2, 2);
          p.ring.material.opacity = 0.3;
          p.ring.scale.set(2.5, 2.5, 2.5);
        } else if (a) {
          p.dot.material.opacity = 0.4;
          p.dot.scale.set(1, 1, 1);
          p.ring.material.opacity = 0.06;
          p.ring.scale.set(1, 1, 1);
        } else {
          const sc = 1 + Math.sin(t * 3 + idx * 1.2) * 0.15;
          p.dot.material.opacity = 0.6;
          p.dot.scale.set(sc, sc, sc);
          p.ring.material.opacity = 0.1;
          p.ring.scale.set(1, 1, 1);
        }
      });
      renderer.render(scene, camera);
    };
    animate();

    sceneRef.current = { renderer, scene };

    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("click", onClick);
      window.removeEventListener("resize", resize);
      renderer.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save the pending pain point.
  const savePending = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const r = await api.pain.add({
        x_norm: pending.x_norm,
        y_norm: pending.y_norm,
        level: formLevel,
        description: formDesc.trim() || undefined,
      });
      setPainPoints((prev) => [...prev, r.point]);
      setPending(null);
    } catch (e) {
      console.error("[pain] save failed", e);
    } finally {
      setSaving(false);
    }
  };

  // Remove (soft-resolve) a saved point.
  const removePoint = async (id: string) => {
    setPainPoints((prev) => prev.filter((p) => p.id !== id));
    try { await api.pain.remove(id); }
    catch (e) { console.error("[pain] remove failed", e); }
  };

  return (
    <div className={_expanded ? "flex flex-col h-full" : "bg-d_card border border-d_border/20 rounded-[10px] p-3 transition-colors hover:border-d_celeste/10 h-full min-h-0 overflow-hidden flex flex-col"}>
      {!_expanded && (
        <div className="font-serif text-[17px] font-semibold mb-2.5 flex items-center justify-between tracking-[0.01em]">
          LifeScribe™ Body
          <ExpandButton onClick={() => expand({ title: "LifeScribe™ Body", render: () => <BodyModelCard ywi={ywi} _expanded /> })} />
        </div>
      )}

      <div className="flex gap-1 mb-1.5">
        {(["view","pain"] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`text-[8px] font-semibold tracking-[.6px] uppercase px-2.5 py-1 rounded border transition ${
              mode === m
                ? "bg-d_accent/10 text-d_accent border-d_accent/20"
                : "border-d_border/20 text-d_text3 hover:text-d_text2"
            }`}
          >
            {m === "view" ? "View" : "Pain"}
          </button>
        ))}
      </div>

      <div ref={containerRef} className="relative flex-1 min-h-0">
        <canvas ref={canvasRef} className="!w-full !h-full block cursor-default" />

        {/* Saved pain dots — ONLY on the Pain tab */}
        {mode === "pain" && painPoints.map((p) => (
          <div
            key={p.id}
            title={`Level ${p.level}${p.description ? " · " + p.description : ""} (tap to remove)`}
            className="absolute rounded-full cursor-pointer z-[5] flex items-center justify-center"
            style={{
              left: `${p.x_norm * 100}%`,
              top: `${p.y_norm * 100}%`,
              width: 18, height: 18,
              transform: "translate(-50%,-50%)",
              background: levelColor(p.level),
              border: `2px solid ${levelColor(p.level)}66`,
              boxShadow: `0 0 10px ${levelColor(p.level)}55`,
            }}
            onClick={(e) => { e.stopPropagation(); removePoint(p.id); }}
          >
            <span className="text-[9px] font-bold text-white leading-none">{p.level}</span>
          </div>
        ))}

        {/* Pending point marker (being placed) */}
        {mode === "pain" && pending && (
          <div
            className="absolute rounded-full z-[6] border-2 border-dashed border-d_text2 animate-pulse"
            style={{ left: `${pending.x_norm * 100}%`, top: `${pending.y_norm * 100}%`, width: 18, height: 18, transform: "translate(-50%,-50%)" }}
          />
        )}

        {/* Pain prompt (only when not actively placing) */}
        {mode === "pain" && !pending && !painLoading && (
          <div className="absolute top-0 left-0 right-0 text-center py-2 text-[10px] text-d_coral bg-d_bg/80 backdrop-blur-sm border-b border-d_coral/20">
            Tap a spot on the body to log pain. Tap a marker to clear it. Resets daily.
          </div>
        )}

        {mode === "pain" && painLoading && (
          <div className="absolute top-0 left-0 right-0 text-center py-2 text-[10px] text-d_text3 bg-d_bg/80 backdrop-blur-sm">
            Loading today's pain log…
          </div>
        )}

        {/* Entry form for the pending point */}
        {mode === "pain" && pending && (
          <div className="absolute inset-x-2 bottom-2 z-[10] bg-d_card border border-d_border/40 rounded-xl shadow-2xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] tracking-[1.5px] uppercase font-bold text-d_coral">Log Pain Point</span>
              <button onClick={() => setPending(null)} className="text-d_text3 hover:text-d_text text-sm leading-none">×</button>
            </div>

            {/* Level 1-10 */}
            <div className="mb-2.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-d_text2 font-semibold">Level</span>
                <span className="text-[13px] font-bold" style={{ color: levelColor(formLevel) }}>{formLevel}/10</span>
              </div>
              <input
                type="range" min={1} max={10} step={1} value={formLevel}
                onChange={(e) => setFormLevel(parseInt(e.target.value, 10))}
                className="w-full accent-d_coral"
                style={{ accentColor: levelColor(formLevel) }}
              />
              <div className="flex justify-between text-[8px] text-d_text3 mt-0.5">
                <span>Mild</span><span>Moderate</span><span>Severe</span>
              </div>
            </div>

            {/* Description */}
            <input
              type="text"
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              placeholder="Describe it (e.g. sharp lower-back ache)"
              maxLength={500}
              className="w-full text-[11px] bg-d_card2/60 border border-d_border/30 rounded-md px-2.5 py-1.5 text-d_text placeholder:text-d_text3 mb-2.5 focus:outline-none focus:border-d_accent/50"
              onKeyDown={(e) => { if (e.key === "Enter") savePending(); }}
              autoFocus
            />

            <div className="flex gap-2">
              <button
                onClick={savePending}
                disabled={saving}
                className="flex-1 text-[11px] font-semibold py-1.5 rounded-md bg-d_coral text-white hover:opacity-90 disabled:opacity-50 transition"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => setPending(null)}
                className="text-[11px] font-semibold py-1.5 px-3 rounded-md border border-d_border/30 text-d_text2 hover:text-d_text transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Tooltip on hover — VIEW mode only */}
        {mode === "view" && active && (
          <div className="absolute bottom-1 left-1 right-1 bg-d_bg/90 border border-d_border/20 rounded-md px-2 py-1.5 backdrop-blur-sm text-[10px] flex items-center gap-1.5">
            <span className="text-sm">{DIM_META[active].icon}</span>
            <strong style={{ color: DIM_META[active].color }}>{DIM_META[active].label}</strong>
            <span className="text-d_text3">—</span>
            <span className="font-serif font-bold" style={{ color: DIM_META[active].color }}>
              {(ywi?.[active] ?? 0).toFixed(1)}/20
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
